import { Alert, Button, Link, Typography } from '@mui/joy';
import { X, Warning, ArrowUpRight, WarningCircle } from '@phosphor-icons/react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { Dialog, DialogBody, DialogContent, DialogHeader, DialogActions, DialogClose } from '~/components/ui/dialog';
import { useTenantSlug } from '~/features/tenant/helpers';
import QueryResolver from '~/services/api/query-resolver';
import { ReportService } from '~/services/api/reports/endpoints';

const PotentialReportLinksDialog = () => {
  const reportId = String(useParams().reportId);
  const slug = useTenantSlug();
  const query = ReportService.endpoints.report.useQuery(
    { tenantSlug: slug, referenceId: reportId },
    { skip: !reportId },
  );
  const reportData = query.data ?? undefined;
  const hasLinkedReports = reportData ? reportData.potential_report_links?.length > 0 : false;
  const [viewLinkedReports, setViewLinkedReports] = useState(false);

  return (
    <QueryResolver
      query={query}
      errorElement={
        <Dialog defaultOpen={query.isError}>
          <DialogContent size='sm'>
            <div className='flex items-center justify-between text-red-500 w-full rounded-md bg-red-50'>
              <div className='flex items-center gap-1'>
                <WarningCircle />
                <Typography color='danger' level='title-sm'>
                  <FormattedMessage defaultMessage='Failed to load potential report links' />
                </Typography>
              </div>
              <Button
                color="danger"
                onClick={() => query.refetch()}
              >
                <FormattedMessage defaultMessage='Try again' />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      }
    >
      <Dialog defaultOpen={hasLinkedReports}>
        <DialogContent size='md' className='items-center'>
          <DialogHeader>
            <nav className='flex items-center justify-end'>
              <DialogClose>
                <X size={24} />
              </DialogClose>
            </nav>
            <div className="flex items-center justify-center">
              <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center">
                <Warning size={28} className='text-white' />
              </div>
            </div>
          </DialogHeader>
          <DialogBody>
            <div className='space-y-2'>
              <Typography textAlign="center" level="title-lg" color='warning'>
                <FormattedMessage defaultMessage='Potential Report Link' />
              </Typography>
              <Typography textAlign="center" level="body-sm"color='warning'>
                <FormattedMessage defaultMessage='This report may be duplicated and potentially linked.' />
              </Typography>
            </div>
          </DialogBody>
          <DialogActions>
            {!viewLinkedReports && (
              <Button
                variant='soft'
                color='secondary'
                fullWidth
                onClick={() => setViewLinkedReports(true)}
                hidden={viewLinkedReports}
              >
                <FormattedMessage defaultMessage='View Linked Reports' />
              </Button>
            )}
            {viewLinkedReports && (
              <div className="flex-1 space-y-3">
                {reportData?.potential_report_links.map((link) => (
                  <Button
                    variant='soft'
                    color='secondary'
                    fullWidth
                    component="a"
                    href={link.reference_id}
                    target='_blank'
                    rel='noreferrer'
                    key={link.formatted_report_number}
                    >
                      <div className='flex flex-row justify-between w-full'>
                        {link.formatted_report_number}
                        <ArrowUpRight size={20}/>
                      </div>
                    </Button>
                ))}
              </div>
            )}
          </DialogActions>
        </DialogContent>
      </Dialog>
    </QueryResolver>
    );
};

export const PotentialReportLinksAlert = () => {
  const reportId = String(useParams().reportId);
  const slug = useTenantSlug();
  const query = ReportService.endpoints.report.useQuery(
    { tenantSlug: slug ?? '', referenceId: reportId },
    { skip: !reportId || !slug },
  );
  const reportData = query.data ?? undefined;
  const hasLinkedReports = reportData ? reportData.potential_report_links?.length > 0 : false;
  const [viewLinkedReports, setViewLinkedReports] = useState(false);

  if (!hasLinkedReports) {
    return (
      <div className='flex justify-center p-5'>
        <Typography level='title-lg'>
          <FormattedMessage defaultMessage='No potential report links found.' />
        </Typography>
      </div>
    );
  }

  return (
    <QueryResolver query={query}>
      <Alert 
        className='border-2 border-orange-300'
        color='warning' 
        variant='soft'
        size='md'
      >
        <div className='flex flex-1 flex-col'>
          <div className='items-center'>
            <Typography level='title-lg' textColor='inherit'>
              <FormattedMessage defaultMessage='Potential Report Link' />
            </Typography>
            <Typography level='body-xs' textColor='inherit'>
              <FormattedMessage defaultMessage='This report may be duplicated and potentially linked.' />
            </Typography>
          </div>
          <div className='flex-col items-start mt-2'>
            <Typography
              component='button'
              textColor='inherit'
              level='title-sm'
              onClick={() => setViewLinkedReports(!viewLinkedReports)}
            >
              {viewLinkedReports ? 'Hide Linked Reports' : 'View Linked Reports'}
            </Typography>
              {viewLinkedReports && (
                reportData?.potential_report_links.map((link) => (
                  <div className='w-full' key={link.formatted_report_number}>
                    <Link
                      className='justify-between px-2 w-full'
                      color='neutral'
                      href={link.reference_id}
                      target='_blank'
                      rel='noreferrer'
                      underline='none'
                    >
                      <Typography level='body-sm'>{link.formatted_report_number}</Typography>
                      <ArrowUpRight size={20} />
                    </Link>
                  </div>
                ))
              )}
            </div>
        </div>
      </Alert>
    </QueryResolver>
  );
};

export default PotentialReportLinksDialog;
